import { DayOfWeek, TemporalAdjusters } from "@js-joda/core";
import type { LocalDate } from "@js-joda/core";

import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import { keys, translate } from "@/plugins/i18n";
import previousOrSame = TemporalAdjusters.previousOrSame;
import nextOrSame = TemporalAdjusters.nextOrSame;
import firstDayOfYear = TemporalAdjusters.firstDayOfYear;
import lastDayOfYear = TemporalAdjusters.lastDayOfYear;
import lastDayOfMonth = TemporalAdjusters.lastDayOfMonth;
import firstDayOfMonth = TemporalAdjusters.firstDayOfMonth;
import type Key from "@/app/types/common/key";
import type { Maybe } from "@/components/utils/typeHelpers";

export interface DateRange {
  from: LocalDate;
  to: LocalDate;
  presetKey?: string;
}

export const dateRangeIsDates = (
  range: DateRange,
  from: LocalDate,
  to: LocalDate
) => range.from.equals(from) && range.to.equals(to);

export const dateRangeIncludes = (
  dateRange: Maybe<DateRange>,
  date: Maybe<LocalDate>
) =>
  !!(
    dateRange &&
    date &&
    !date.isBefore(dateRange.from) &&
    !date.isAfter(dateRange.to)
  );

export class DateRangePreset {
  private _key: string;
  private _label: string;
  private _range: () => [LocalDate, LocalDate];
  constructor(t: Key, range: () => [LocalDate, LocalDate]) {
    this._key = t.value;
    this._label = translate(t);
    this._range = range;
  }

  public get key() {
    return this._key;
  }

  public get label() {
    return this._label;
  }

  public getRange() {
    return this._range();
  }

  public asDateRange(): DateRange {
    const range = this.getRange();
    return {
      from: range[0],
      to: range[1],
      presetKey: this._key,
    };
  }
}

export const today = new DateRangePreset(keys.date_range_today, () => [
  tenantService.today,
  tenantService.today,
]);

export const yesterday = new DateRangePreset(keys.date_range_yesterday, () => [
  tenantService.today.minusDays(1),
  tenantService.today.minusDays(1),
]);

export const lastSevenDays = new DateRangePreset(
  keys.date_range_last_seven_days,
  () => [tenantService.today.minusDays(6), tenantService.today]
);

export const lastThirtyDays = new DateRangePreset(
  keys.date_range_last_thirty_days,
  () => [tenantService.today.minusDays(29), tenantService.today]
);

export const lastSixtyDays = new DateRangePreset(
  keys.date_range_last_sixty_days,
  () => [tenantService.today.minusDays(59), tenantService.today]
);

export const thisWeek = new DateRangePreset(keys.date_range_this_week, () => [
  tenantService.today.with(previousOrSame(DayOfWeek.MONDAY)),
  tenantService.today.with(nextOrSame(DayOfWeek.SUNDAY)),
]);

export const lastWeek = new DateRangePreset(keys.date_range_last_week, () => [
  tenantService.today.minusWeeks(1).with(previousOrSame(DayOfWeek.MONDAY)),
  tenantService.today.minusWeeks(1).with(nextOrSame(DayOfWeek.SUNDAY)),
]);

export const thisMonth = new DateRangePreset(keys.date_range_this_month, () => [
  tenantService.today.with(firstDayOfMonth()),
  tenantService.today.with(lastDayOfMonth()),
]);

export const lastMonth = new DateRangePreset(keys.date_range_last_month, () => [
  tenantService.today.minusMonths(1).with(firstDayOfMonth()),
  tenantService.today.minusMonths(1).with(lastDayOfMonth()),
]);

export const thisYear = new DateRangePreset(keys.date_range_this_year, () => [
  tenantService.today.with(firstDayOfYear()),
  tenantService.today.with(lastDayOfYear()),
]);

export const lastYear = new DateRangePreset(keys.date_range_last_year, () => [
  tenantService.today.minusYears(1).with(firstDayOfYear()),
  tenantService.today.minusYears(1).with(lastDayOfYear()),
]);

export const nextWeek = new DateRangePreset(keys.date_range_next_week, () => [
  tenantService.today.plusWeeks(1).with(previousOrSame(DayOfWeek.MONDAY)),
  tenantService.today.plusWeeks(1).with(nextOrSame(DayOfWeek.SUNDAY)),
]);

export const nextMonth = new DateRangePreset(keys.date_range_next_month, () => [
  tenantService.today.plusMonths(1).with(firstDayOfMonth()),
  tenantService.today.plusMonths(1).with(lastDayOfMonth()),
]);

export const nextYear = new DateRangePreset(keys.date_range_next_year, () => [
  tenantService.today.plusYears(1).with(firstDayOfYear()),
  tenantService.today.plusYears(1).with(lastDayOfYear()),
]);

export const tomorrow = new DateRangePreset(keys.date_range_tomorrow, () => [
  tenantService.today.plusDays(1),
  tenantService.today.plusDays(1),
]);

/**
 * Array of presets to display in the date range control
 */

export const standardPresets: DateRangePreset[] = [
  today,
  yesterday,
  lastSevenDays,
  lastThirtyDays,
  lastSixtyDays,
  thisWeek,
  lastWeek,
  thisMonth,
  lastMonth,
  thisYear,
  lastYear,
  tomorrow,
  nextWeek,
  nextMonth,
];

export const pastOnlyPresets: DateRangePreset[] = [
  today,
  yesterday,
  lastSevenDays,
  lastThirtyDays,
  lastSixtyDays,
  thisWeek,
  lastWeek,
  thisMonth,
  lastMonth,
  thisYear,
  lastYear,
];

export const forwardFocussedPresets: DateRangePreset[] = [
  today,
  tomorrow,
  nextWeek,
  nextMonth,
  nextYear,
  thisWeek,
  thisMonth,
  thisYear,
  yesterday,
  lastSevenDays,
  lastThirtyDays,
];

export const allPresets: DateRangePreset[] = [...standardPresets, nextYear];

export default standardPresets;
